import { CustomerAuthInputField } from '../form-sections'
import type { CustomerAuthFieldUiHelpers } from '../form-sections'
import type { RequirementTone } from './ActivationFlow.types'

type PasswordRequirement = {
  label: string
  tone: RequirementTone
}

type ActivationSetupViewProps = {
  customerAreaLabel: string
  cardTitle: string
  cardSubtitle: string
  sectionPasswordTitle: string
  passwordLabel: string
  passwordPlaceholder: string
  passwordShowLabel: string
  passwordHideLabel: string
  confirmPasswordLabel: string
  confirmPasswordPlaceholder: string
  requirementsTitle: string
  requirements: PasswordRequirement[]
  passwordValidLabel: string
  showPasswordValid: boolean
  sectionSecurityTitle: string
  sectionSecuritySubtitle: string
  sectionTwoFactorTitle: string
  sectionTwoFactorSubtitle: string
  optionalBadge: string
  twoFactorFoundationNote: string
  submitCta: string
  submittingCta: string
  profileSettingsNote: string
  trustFooter: string
  backToLoginLabel: string
  loginHref: string
  password: string
  confirmPassword: string
  submitting: boolean
  activationFieldsValid: boolean
  formError: string
  fieldUi: CustomerAuthFieldUiHelpers
  onPasswordChange: (value: string) => void
  onConfirmPasswordChange: (value: string) => void
  onSubmit: () => void
}

const requirementRowClass = (tone: RequirementTone) =>
  tone === 'met'
    ? 'text-(--color-status-success)'
    : tone === 'error'
      ? 'text-(--color-status-error)'
      : 'text-(--color-text-muted)'

export const ActivationSetupView = ({
  customerAreaLabel,
  cardTitle,
  cardSubtitle,
  sectionPasswordTitle,
  passwordLabel,
  passwordPlaceholder,
  passwordShowLabel,
  passwordHideLabel,
  confirmPasswordLabel,
  confirmPasswordPlaceholder,
  requirementsTitle,
  requirements,
  passwordValidLabel,
  showPasswordValid,
  sectionSecurityTitle,
  sectionSecuritySubtitle,
  sectionTwoFactorTitle,
  sectionTwoFactorSubtitle,
  optionalBadge,
  twoFactorFoundationNote,
  submitCta,
  submittingCta,
  profileSettingsNote,
  trustFooter,
  backToLoginLabel,
  loginHref,
  password,
  confirmPassword,
  submitting,
  activationFieldsValid,
  formError,
  fieldUi,
  onPasswordChange,
  onConfirmPasswordChange,
  onSubmit,
}: ActivationSetupViewProps) => (
  <form
    className="space-y-5"
    onSubmit={(event) => {
      event.preventDefault()
      void onSubmit()
    }}
  >
    <div className="space-y-2">
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-(--color-status-info)">{customerAreaLabel}</p>
      <h2 className="text-base font-semibold tracking-tight text-(--color-text-primary)">{cardTitle}</h2>
      <p className="text-sm leading-relaxed text-(--color-text-secondary)">{cardSubtitle}</p>
    </div>

    <div className="space-y-4 rounded-xl border border-[color-mix(in_srgb,var(--color-border-default)_78%,transparent)] bg-[color-mix(in_srgb,var(--color-bg-page-secondary)_52%,var(--color-bg-card))] p-4">
      <h3 className="text-sm font-semibold text-(--color-text-primary)">{sectionPasswordTitle}</h3>

      <div className="space-y-3">
        <CustomerAuthInputField
          field="password"
          label={passwordLabel}
          type="password"
          value={password}
          onChange={onPasswordChange}
          placeholder={passwordPlaceholder}
          autoComplete="new-password"
          required
          disabled={submitting}
          allowPasswordToggle
          showPasswordLabel={passwordShowLabel}
          hidePasswordLabel={passwordHideLabel}
          ui={fieldUi}
        />

        <CustomerAuthInputField
          field="confirmPassword"
          label={confirmPasswordLabel}
          type="password"
          value={confirmPassword}
          onChange={onConfirmPasswordChange}
          placeholder={confirmPasswordPlaceholder}
          autoComplete="new-password"
          required
          disabled={submitting}
          allowPasswordToggle
          showPasswordLabel={passwordShowLabel}
          hidePasswordLabel={passwordHideLabel}
          ui={fieldUi}
        />
      </div>

      <div className="space-y-2">
        <p className="text-xs font-semibold text-(--color-text-secondary)">{requirementsTitle}</p>
        <ul className="space-y-1.5 text-xs">
          {requirements.map((requirement) => (
            <li
              key={requirement.label}
              className={`flex items-center gap-2 rounded-lg px-2 py-1 ${requirementRowClass(requirement.tone)}`}
            >
              <span className="inline-flex h-4 w-4 items-center justify-center rounded-full border border-current text-[10px]">
                {requirement.tone === 'met' ? '✓' : '•'}
              </span>
              <span>{requirement.label}</span>
            </li>
          ))}
        </ul>
      </div>

      {showPasswordValid ? <p className="text-xs font-semibold text-(--color-status-success)">{passwordValidLabel}</p> : null}
    </div>

    <div className="space-y-3 rounded-xl border border-[color-mix(in_srgb,var(--color-border-default)_78%,transparent)] bg-[color-mix(in_srgb,var(--color-bg-page-secondary)_52%,var(--color-bg-card))] p-4">
      <h3 className="text-sm font-semibold text-(--color-text-primary)">{sectionSecurityTitle}</h3>
      <p className="text-xs leading-relaxed text-(--color-text-secondary)">{sectionSecuritySubtitle}</p>
    </div>

    <div className="space-y-3 rounded-xl border border-[color-mix(in_srgb,var(--color-border-default)_78%,transparent)] bg-[color-mix(in_srgb,var(--color-bg-page-secondary)_52%,var(--color-bg-card))] p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-(--color-text-primary)">{sectionTwoFactorTitle}</h3>
          <p className="mt-1 text-xs leading-relaxed text-(--color-text-secondary)">{sectionTwoFactorSubtitle}</p>
        </div>
        <span className="rounded-full border border-[color-mix(in_srgb,var(--color-border-default)_82%,transparent)] bg-(--color-bg-card) px-2 py-0.5 text-[11px] font-semibold text-(--color-text-muted)">
          {optionalBadge}
        </span>
      </div>

      <p className="text-xs leading-relaxed text-(--color-text-muted)">{twoFactorFoundationNote}</p>
    </div>

    {formError ? <p className="text-sm leading-relaxed text-(--color-status-error)">{formError}</p> : null}

    <button
      type="submit"
      disabled={submitting || !activationFieldsValid}
      className="ca-btn-primary inline-flex h-11 w-full items-center justify-center px-4 text-sm font-semibold shadow-[0_10px_24px_-18px_color-mix(in_srgb,var(--color-action-primary)_85%,transparent)] transition-colors duration-200 disabled:cursor-not-allowed disabled:opacity-60"
    >
      {submitting ? submittingCta : submitCta}
    </button>

    <p className="text-center text-[12px] text-(--color-text-muted)">{profileSettingsNote}</p>

    <p className="text-center text-[12px] font-medium text-(--color-text-secondary)">{trustFooter}</p>

    <a
      href={loginHref}
      className="inline-flex w-full items-center justify-center text-sm font-semibold text-(--color-focus-ring) underline decoration-[color-mix(in_srgb,var(--color-focus-ring)_35%,transparent)] underline-offset-4 transition-colors duration-150 hover:text-(--color-text-primary)"
    >
      {backToLoginLabel}
    </a>
  </form>
)
